import Resources from "./study_plan_task_resources";
import Icon from "components/pages/study_plan/icon";
import FloatingMenu from "components/floating_menu";
import { FiMoreHorizontal, FiEye, FiEyeOff, FiLoader } from "react-icons/fi";

import { track } from "utils/tracker";
import clsx from "clsx";

import { useState, useEffect } from "react";
import { useIgnore } from "hooks/ignore";
import { useNotifications } from "hooks/notifications";
import { useUser } from "hooks/user";

type StudyPlanTaskProps = {
  task: any;
  isLoading?: boolean;
  opacity?: string;
};

export default function StudyPlanTask(props: StudyPlanTaskProps): JSX.Element {
  const { task, isLoading, opacity } = props;

  const {
    ignoreTopic,
    deleteTopicIgnore,
    invalidateTasksQuery,
    isLoading: isIgnoringTopic,
    response: topicIgnoreResponse
  } = useIgnore();
  const [showMenu, setShowMenu] = useState(false);
  const { setNotification } = useNotifications();
  const { user } = useUser();

  const borderColors = {
    "border-t-subjects-exact":
      task.topic.parent.group.slug == "ciencias-exatas",
    "border-t-subjects-human":
      task.topic.parent.group.slug == "ciencias-humanas",
    "border-t-subjects-languages": task.topic.parent.group.slug == "linguagens"
  };

  const iconColors = {
    "text-subjects-exact": task.topic.parent.group.slug == "ciencias-exatas",
    "text-subjects-human": task.topic.parent.group.slug == "ciencias-humanas",
    "text-subjects-languages": task.topic.parent.group.slug == "linguagens"
  };

  const iconViewBox = {
    geografia: "0 -5 85 84",
    quimica: "4 -6 96 94",
    matematica: "0 -20 95 95",
    literatura: "0 -5 90 92",
    filosofia: "0 -15 89 90",
    sociologia: "5 -10 84 94"
  };

  useEffect(() => {
    if (topicIgnoreResponse && topicIgnoreResponse.status === "ok") {
      if (task.ignoredTopicId) {
        setEnableNotification();
      } else {
        setDisableNotification();
      }
      setShowMenu(false);
    }
  }, [topicIgnoreResponse]);

  function trackClick(type: string) {
    track("Clicked on study plan task resource", { type });
  }

  function toggleTopicIgnore() {
    if (task.ignoredTopicId) {
      deleteTopicIgnore(task.ignoredTopicId).then(invalidateTasksQuery);
    } else {
      ignoreTopic(task.topic.slug, task.topic.parent.slug).then(
        invalidateTasksQuery
      );
    }
  }

  function setEnableNotification() {
    setNotification({
      type: "success",
      title: "Tópico reativado",
      text: "O assunto voltará a ser considerado no plano de estudos"
    });
  }

  function setDisableNotification() {
    setNotification({
      type: "success",
      title: "Tópico ignorado",
      text: "O assunto não será mais considerado no plano de estudos"
    });
  }

  return (
    <div
      className={clsx(
        "fade-loading task p-5 border-t-2 rounded-b-md shadow hover:opacity-100 transition-opacity",
        borderColors,
        opacity,
        { loading: isLoading, "!opacity-100": isIgnoringTopic }
      )}
    >
      <div className="flex items-start justify-between mb-4 w-full">
        <div className="flex items-center">
          <Icon
            slug={task.topic.parent.slug}
            width={20}
            height={20}
            viewBox={iconViewBox[task.topic.parent.slug]}
            className={clsx("mr-3", iconColors)}
          />

          <span className="block text-emphasis-medium text-lg font-semibold">
            {task.topic.parent.name} - {task.topic.name}
          </span>
        </div>

        {user && (
          <>
            <div
              className="menu-trigger ml-2 cursor-pointer"
              onClick={() => setShowMenu(true)}
            >
              <FiMoreHorizontal size={20} />
            </div>
            {showMenu && (
              <FloatingMenu
                onChange={(visible: boolean) => setShowMenu(visible)}
              >
                <li onClick={toggleTopicIgnore} className="flex items-center">
                  {isIgnoringTopic ? (
                    <FiLoader className="animate-spin" />
                  ) : task.ignoredTopicId ? (
                    <FiEye />
                  ) : (
                    <FiEyeOff />
                  )}

                  <span>
                    {task.ignoredTopicId ? "Reativar" : "Ignorar"} tarefa
                  </span>
                </li>
              </FloatingMenu>
            )}
          </>
        )}
      </div>

      <div className="mb-[10px]" onClick={() => trackClick("lessons")}>
        <Resources task={task} type="lessons" />
      </div>
      <div onClick={() => trackClick("questions")}>
        <Resources task={task} type="questions" />
      </div>
    </div>
  );
}
